import { DetailedSurvey } from "@survey-types/survey.core.types";
import { Property } from "@survey-types/survey.property.types";
import calculateContentSpaceRequirement, {
  analyzeContent,
} from "./calculateContentSpaceRequirement";
import {
  FIRST_PAGE_LIMIT,
  splitLongContent,
  SUBSEQUENT_PAGE_LIMIT,
} from "./contentSplitting";
import { LeftContentItem } from "./heightPagination";

// Removed local definition of processPropertyFields; now imported from propertyFieldUtils.ts

export const processLeftColumnContent = (
  property: Property,
  isLandscape: boolean,
  survey: DetailedSurvey | null,
): LeftContentItem[] => {
  const leftContent: LeftContentItem[] = [];

  // Use propertyFieldRuleList from survey to determine which fields to show and their order
  const propertyFieldRules =
    survey?.surveyPropertyFieldRule?.propertyFieldRuleList || [];
  // Check if comments and amenities are enabled based on propertyFieldRules
  const enabledFields = propertyFieldRules
    .filter((rule) => !rule.isHidden)
    .filter(
      (rule) => rule.fieldKey === "comments" || rule.fieldKey === "amenities",
    );

  const isCommentsEnabled = enabledFields.some(
    (rule) => rule.fieldKey === "comments",
  );
  const isAmenitiesEnabled = enabledFields.some(
    (rule) => rule.fieldKey === "amenities",
  );

  // Analyze the content first with filtered property
  const analysis = analyzeContent(property);
  if (analysis.strategy === "no-content") {
    return [];
  }

  // Define page limits based on orientation
  const firstPageLimit = isLandscape
    ? FIRST_PAGE_LIMIT.landscape
    : FIRST_PAGE_LIMIT.portrait;

  // Process comments first if they exist
  if (analysis.hasComments && isCommentsEnabled) {
    const commentsText = property.comments!.trim();
    // Comments always start with first page treatment since they come first
    const commentsChunks = splitLongContent(commentsText, {
      isFirstPageContent: true,
      isLandscape,
    });
    commentsChunks.forEach((chunk, index) => {
      if (chunk.trim()) {
        const title = index === 0 ? "Comments" : "Comments (continued)";
        leftContent.push({
          type: "comments",
          title,
          content: chunk.trim(),
        });
      }
    });
  }

  // Process amenities if they exist
  if (analysis.hasAmenities && isAmenitiesEnabled) {
    const amenitiesText = property.amenities!.trim();
    if (analysis.strategy === "amenities-only") {
      // No comments, amenities is the first content - use normal splitting
      const amenitiesChunks = splitLongContent(amenitiesText, {
        isFirstPageContent: true,
        isLandscape,
      });
      amenitiesChunks.forEach((chunk, index) => {
        if (chunk.trim()) {
          const title = index === 0 ? "Amenities" : "Amenities (continued)";
          leftContent.push({
            type: "amenities",
            title,
            content: chunk.trim(),
          });
        }
      });
    } else if (analysis.strategy === "both-sections") {
      // We have comments, use smart chunking to optimize space usage across all pages

      // First, split comments to see how many chunks we'll have
      const commentsChunks = splitLongContent(property.comments!.trim(), {
        isFirstPageContent: true,
        isLandscape,
      });

      // Now implement smart chunking for amenities across all pages
      const amenitiesChunks: string[] = [];
      let remainingAmenities = amenitiesText;
      let pageIndex = 0;

      while (remainingAmenities.length > 0 && pageIndex < 10) {
        // Safety limit
        let availableSpace = 0;

        if (pageIndex === 0) {
          // First page: calculate space after comments
          const commentsSpaceRequired = calculateContentSpaceRequirement(
            commentsChunks[0]?.length || 0,
            true, // is first section
          );
          const commentsFirstPageUsage = Math.min(
            commentsSpaceRequired,
            firstPageLimit,
          );
          const remainingFirstPageSpace = Math.max(
            0,
            firstPageLimit - commentsFirstPageUsage,
          );
          availableSpace = remainingFirstPageSpace - 70; // Buffer for amenities header + formatting
        } else if (pageIndex < commentsChunks.length) {
          // Subsequent page with comments continuation
          const commentsChunkIndex = pageIndex;
          if (commentsChunkIndex < commentsChunks.length) {
            const commentsSpaceRequired = calculateContentSpaceRequirement(
              commentsChunks[commentsChunkIndex].length,
              false, // not first section (continued)
            );
            const subsequentPageLimit = isLandscape
              ? SUBSEQUENT_PAGE_LIMIT.landscape
              : SUBSEQUENT_PAGE_LIMIT.portrait;
            const commentsPageUsage = Math.min(
              commentsSpaceRequired,
              subsequentPageLimit,
            );
            const remainingPageSpace = Math.max(
              0,
              subsequentPageLimit - commentsPageUsage,
            );
            availableSpace = remainingPageSpace - 70; // Buffer for amenities header + formatting
          }
        } else {
          // Pure amenities page
          availableSpace =
            (isLandscape
              ? SUBSEQUENT_PAGE_LIMIT.landscape
              : SUBSEQUENT_PAGE_LIMIT.portrait) - 50; // Full page minus header
        }

        if (availableSpace > 400) {
          // Only split if there's substantial space
          // Meaningful space available
          // Try to fit amenities chunk in available space
          let chunkSize = Math.min(remainingAmenities.length, availableSpace);

          if (remainingAmenities.length <= availableSpace) {
            // All remaining amenities fit
            amenitiesChunks.push(remainingAmenities);
            break;
          } else {
            // Split at natural break points
            const paragraphBreak = remainingAmenities.lastIndexOf(
              "\n\n",
              chunkSize,
            );
            if (paragraphBreak > chunkSize * 0.3) {
              chunkSize = paragraphBreak + 2;
            } else {
              const sentenceBreak = remainingAmenities.lastIndexOf(
                ". ",
                chunkSize,
              );
              if (sentenceBreak > chunkSize * 0.3) {
                chunkSize = sentenceBreak + 2;
              } else {
                const wordBreak = remainingAmenities.lastIndexOf(
                  " ",
                  chunkSize,
                );
                if (wordBreak > chunkSize * 0.3) {
                  chunkSize = wordBreak + 1;
                }
              }
            }

            const chunk = remainingAmenities.substring(0, chunkSize).trim();
            amenitiesChunks.push(chunk);
            remainingAmenities = remainingAmenities.substring(chunkSize).trim();
          }
        } else {
          // Not enough space on this page, continue to next page
        }

        pageIndex++;

        // If we've exhausted reasonable pages, fall back to standard chunking
        if (pageIndex >= 5) {
          const remainingChunks = splitLongContent(remainingAmenities, {
            isFirstPageContent: false,
            isLandscape,
          });
          amenitiesChunks.push(...remainingChunks);
          break;
        }
      }

      // Add amenities chunks to content
      amenitiesChunks.forEach((chunk, index) => {
        if (chunk.trim()) {
          const title = index === 0 ? "Amenities" : "Amenities (continued)";
          leftContent.push({
            type: "amenities",
            title,
            content: chunk.trim(),
          });
        }
      });
    }
  }
  return leftContent;
};
